import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useSEO } from '../../hooks/useSEO';
import { Crosshair, Users, ShieldCheck, Settings, ArrowRight } from 'lucide-react';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';

const pillars = [
  { icon: Crosshair, title: 'Diagnóstico de Criterio', desc: 'Análisis de las tensiones actuales de su negocio.' },
  { icon: Users, title: 'Selección bajo criterio profesional', desc: 'Identificación manual de los 3 perfiles con mayor encaje.' },
  { icon: ShieldCheck, title: 'Entorno Blindado', desc: 'Toda la información compartida está protegida bajo nuestro protocolo de confidencialidad.' },
];

const features = [
  { icon: Crosshair, title: 'Diagnóstico Estratégico', desc: 'Analizamos el contexto y la necesidad antes de proponer perfiles.' },
  { icon: Users, title: 'Top 3 Perfiles', desc: 'Presentamos únicamente los profesionales con mayor encaje.' },
  { icon: ShieldCheck, title: 'Confidencialidad', desc: 'NDA-first y protección integral de la información.' },
  { icon: Settings, title: 'Modelo Flexible', desc: 'Pago vinculado a resultados y sin estructuras innecesarias.' },
];

const faqs = [
  { q: '¿Cuánto tarda el proceso?', a: 'Diagnóstico en menos de 48 horas. Presentación de perfiles al instante después del diagnóstico.' },
  { q: '¿Cuánto cuesta?', a: 'Success Fee únicamente cuando el mandato se formaliza. Sin costes anticipados.' },
  { q: '¿Qué tipo de profesionales puedo encontrar?', a: 'Perfiles con experiencia en finanzas, operaciones, crecimiento, gobernanza, internacionalización, transformación, sucesión, conocimiento sectorial específico, entre otros. La red se adapta a cada necesidad.' },
];

export default function EmpresaPage() {
  const navigate = useNavigate();
  const sectionRef = useScrollAnimation();
  useSEO({
    title: 'Para Empresas | Conadvy',
    description: 'Acceda a advisors de primer nivel para sus decisiones estratégicas. Proceso confidencial, perfiles verificados, sin compromiso fijo.',
    path: '/empresa',
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="empresa-page" className="min-h-screen bg-[#F5F5F3]">
      <Header variant="subpage" />

      {/* Hero + 3 Pillars (shared background) */}
      <div className="relative overflow-hidden" style={{ '--bg-img-opacity': 1, '--bg-veil-top': 'rgba(248,248,246,0.10)', '--bg-veil-bottom': 'rgba(248,248,246,0.22)' }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(/images/empresas-sala-consejo.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 'var(--bg-img-opacity)',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, var(--bg-veil-top), var(--bg-veil-bottom))' }}
        />
        <section className="pt-32 pb-20 md:pt-36 md:pb-24 relative z-[2]">
        <div className="text-center" style={{ maxWidth: '56rem', margin: '0 auto', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="gold-line" style={{ background: '#B9884A' }} />
            <span className="text-[#B9884A] text-xs font-medium tracking-[0.2em] uppercase">Para Empresas</span>
            <div className="gold-line" style={{ background: '#B9884A' }} />
          </div>
          <h1
            data-testid="empresa-page-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-[0.02em] leading-tight mb-6"
            style={{ color: '#0C1730' }}
          >
            Encuentre el profesional adecuado sin perder tiempo
          </h1>
          <p
            data-testid="empresa-page-subheadline"
            className="text-base md:text-lg font-normal leading-relaxed max-w-2xl mx-auto"
            style={{ color: '#FFFFFF' }}
          >
            Acceda a experiencia senior mediante un proceso confidencial,
            estructurado y diseñado para generar impacto real.
          </p>
        </div>
        </section>

        {/* 3 Pillars */}
        <section ref={sectionRef} className="pb-12 md:pb-16 relative z-[2]">
        <div style={{ maxWidth: '64rem', margin: '0 auto', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <div
                key={p.title}
                data-testid={`empresa-pillar-${i}`}
                className={`fade-up fade-up-delay-${i + 1} bg-white border border-[#00163E]/10 p-8 md:p-10 rounded-sm text-center hover:shadow-lg hover:shadow-[#00163E]/5 transition-all duration-300`}
              >
                <div className="w-14 h-14 bg-[#00163E]/5 rounded-full flex items-center justify-center mx-auto mb-6">
                  <p.icon size={24} className="text-[#C58C40]" strokeWidth={1.5} />
                </div>
                <h3 className="text-[#00163E] text-lg font-medium mb-3">{p.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
          {/* CTA below pillars */}
          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/empresa/registro')}
              data-testid="empresa-pillar-cta"
              className="inline-flex items-center gap-3 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-10 py-4 text-sm tracking-[0.15em] uppercase group"
            >
              <span>Iniciar Mandato</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
        </section>
      </div>

      {/* Perspective */}
      <section className="bg-[#00163E] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00163E] to-[#001F4F]" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white text-xl md:text-2xl font-normal leading-relaxed mb-4">
            Las mejores decisiones suelen empezar con una perspectiva diferente.
          </p>
          <p className="text-[#8A9BB1] text-base leading-relaxed max-w-2xl mx-auto">
            Conadvy facilita el acceso a experiencia contrastada y conocimiento
            especializado cuando necesita avanzar con mayor claridad, velocidad y confianza.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="space-y-0">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`empresa-feature-${i}`}
                className={`flex gap-6 py-8 ${i < features.length - 1 ? 'border-b border-[#00163E]/10' : ''}`}
              >
                <f.icon size={20} className="text-[#C58C40] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <h3 className="text-[#00163E] text-base font-medium mb-1">{f.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block mb-6">
            Preguntas frecuentes
          </span>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#00163E]/10">
                <AccordionTrigger className="faq-trigger text-[#00163E] text-base font-normal py-5 hover:no-underline hover:text-[#C58C40] transition-colors">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-500 text-sm leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Modelo de participación */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="gold-line" />
            <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase">
              Modelo de Participación
            </span>
          </div>
          <div className="bg-white border border-[#00163E]/10 p-8 md:p-10 rounded-sm">
            <h4 className="text-[#00163E] text-lg font-medium mb-2">Success Match Fee</h4>
            <p className="text-[#C58C40] text-sm font-medium mb-4">Solo se cobra cuando hay conexión exitosa.</p>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Conadvy aplica un modelo de tarifa escalonada vinculada al tamaño de la empresa.
              El fee se activa únicamente cuando el mandato se formaliza y la conexión entre empresa
              y advisor se materializa con éxito. Sin costes fijos, sin compromisos previos.
            </p>
            <div className="h-[1px] bg-[#00163E]/10 mb-6" />
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C58C40] mt-1.5 flex-shrink-0" />
                <p className="text-[#00163E]/70 text-sm">Tarifa escalonada según el tamaño de la empresa.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C58C40] mt-1.5 flex-shrink-0" />
                <p className="text-[#00163E]/70 text-sm">Acceso a criterio senior sin estructura fija ni costes recurrentes.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C58C40] mt-1.5 flex-shrink-0" />
                <p className="text-[#00163E]/70 text-sm">Matching validado y confidencial. Coste asociado únicamente a resultados.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <button
            onClick={() => navigate('/empresa/registro')}
            data-testid="empresa-registro-cta"
            className="inline-flex items-center gap-3 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-10 py-4 text-sm tracking-[0.15em] uppercase group"
          >
            <span>Comenzar Diagnóstico</span>
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <p className="text-[#8A9BB1] text-xs mt-4">
            Proceso confidencial. Respuesta en menos de 48 horas.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
